import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ClipboardList, Link2, Pencil, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useSurveyTemplates } from '@/features/surveys/api/use-survey-templates';
import { CreateCampaignDialog } from '@/features/surveys/components/CreateCampaignDialog';
import { TEMPLATE_CATEGORY_KEYS, type SurveyTemplateListItem } from '@/features/surveys/types';

export const Route = createFileRoute('/_authenticated/surveys/')({
  validateSearch: (search: Record<string, unknown>) => ({
    tab: search.tab === 'custom' || search.tab === 'system' ? search.tab : 'all',
  }),
  component: SurveyTemplatesPage,
});

function SurveyTemplatesPage() {
  const { t } = useTranslation('surveys');
  const navigate = useNavigate();
  const { tab } = Route.useSearch();
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState(tab);
  const [shareTemplate, setShareTemplate] = useState<SurveyTemplateListItem | null>(null);

  const params: Record<string, string> = { page: '1', page_size: '20' };
  if (search) params.search = search;
  if (activeTab === 'system') params.is_system = 'true';
  if (activeTab === 'custom') params.is_system = 'false';

  const { data, isLoading } = useSurveyTemplates(params);

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('templates.title')}</h1>
          <p className="text-muted-foreground">{t('templates.description')}</p>
          {/*
            TODO(WP4-B): Render <GroupScopePill /> on the surveys dashboard
            once `useSurveyTemplates` / `useSurveyCampaigns` surface the
            rollup scope metadata returned by the backend
            (`SurveyCampaignListResponse` / `SurveyResponseListResponse`
            already inherit `RollupScopeMixin`). Skipped today because the
            frontend hooks do not propagate those fields yet. See
            `docs/subsidiary-refactor/MIGRATION-GUIDE.md`.
          */}
        </div>
        <Link to="/surveys/templates/new">
          <Button data-testid="new-template-button">
            <Plus className="mr-2 h-4 w-4" />
            {t('templates.newTemplate')}
          </Button>
        </Link>
      </div>

      <div className="flex items-center gap-4">
        <Input
          placeholder={t('templates.searchPlaceholder')}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
          }}
          className="max-w-sm"
          data-testid="template-search"
        />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all" data-testid="tab-all">
            {t('templates.tabAll')}
          </TabsTrigger>
          <TabsTrigger value="custom" data-testid="tab-custom">
            {t('templates.tabCustom')}
          </TabsTrigger>
          <TabsTrigger value="system" data-testid="tab-system">
            {t('templates.tabSystem')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab}>
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : !data?.items.length ? (
            <EmptyState
              icon={ClipboardList}
              title={t('templates.emptyTitle')}
              description={t('templates.emptyDescription')}
              action={{
                label: t('templates.newTemplate'),
                onClick: () => void navigate({ to: '/surveys/templates/new' }),
              }}
            />
          ) : (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('templates.name')}</TableHead>
                      <TableHead>{t('templates.category')}</TableHead>
                      <TableHead>{t('templates.status')}</TableHead>
                      <TableHead>{t('templates.questions')}</TableHead>
                      <TableHead>{t('templates.version')}</TableHead>
                      <TableHead>{t('templates.lastModified')}</TableHead>
                      <TableHead className="w-[220px] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.items.map((template: SurveyTemplateListItem) => (
                      <TableRow key={template.id} data-testid={`template-row-${template.id}`}>
                        <TableCell>
                          <Link
                            to="/surveys/templates/$templateId"
                            params={{ templateId: template.id }}
                            className="font-medium hover:underline"
                          >
                            {template.name}
                          </Link>
                          {template.isSystem && (
                            <Badge variant="outline" className="ml-2">
                              {t('templates.system')}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {t(`categoryLabels.${TEMPLATE_CATEGORY_KEYS[template.category]}`)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              template.status === 'published'
                                ? 'default'
                                : template.status === 'draft'
                                  ? 'outline'
                                  : 'secondary'
                            }
                          >
                            {template.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{template.questionCount}</TableCell>
                        <TableCell>v{template.version}</TableCell>
                        <TableCell>{new Date(template.updatedAt).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={template.status !== 'published'}
                              onClick={() => {
                                setShareTemplate(template);
                              }}
                            >
                              <Link2 className="mr-1.5 h-3.5 w-3.5" />
                              Share link
                            </Button>
                            <Link
                              to="/surveys/templates/$templateId"
                              params={{ templateId: template.id }}
                            >
                              <Button variant="ghost" size="sm">
                                <Pencil className="mr-1.5 h-3.5 w-3.5" />
                                {t('templates.edit', 'Edit')}
                              </Button>
                            </Link>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      <CreateCampaignDialog
        isOpen={Boolean(shareTemplate)}
        onClose={() => {
          setShareTemplate(null);
        }}
        preselectedTemplateId={shareTemplate?.id}
        onSuccess={(campaign) => {
          void navigate({
            to: '/surveys/campaigns/$campaignId',
            params: { campaignId: campaign.id },
          });
        }}
      />
    </div>
  );
}
